import { get } from "lodash-es";
import { useContext } from "react";
import type { Doc } from "sharedb/lib/client";

import type { FieldType, PathType, WorksheetType } from "../../shared/types/worksheet";

import { DocViewContext } from "providers/DocViewProvider";

/**
 * This hook generates a unique, stable HTML id for a field based on its path and the current context.
 * If you just want a random ID, you can use `useId()` from React instead.
 * This hook is for generating stable IDs that can be referenced by other fields in the worksheet.
 * @param path The path to the field in the worksheet data structure.
 */
export const useFieldHtmlId = (doc: Doc<WorksheetType>, path: PathType, fieldPath?: PathType) => {
  const field = get(doc.data, fieldPath ?? path) as FieldType | undefined;
  const { showResponses, isFeaturing, isTemplate } = useContext(DocViewContext);

  // If we are showing multiple responses, we need to use the full path to the field to ensure uniqueness,
  // since that embeds the student/group ID of the response.
  // But if we are *not* showing multiple responses, we can just use the field's ID by itself.
  const baseId = showResponses ? path.join(".") : field ? field.id : path.join(".");

  // Sometimes the same field is rendered multiple times on the same page,
  // so we need suffixes to ensure uniqueness.
  // - If the template response is shown, we add a ":t" suffix.
  // - If the field is being featured, we add a ":f" suffix.
  return `${baseId}${isTemplate ? ":t" : ""}${isFeaturing ? ":f" : ""}`;
};
